import type { ParsedGlbBinary } from './types';

const GLB_HEADER_LENGTH = 12;
const GLB_CHUNK_HEADER_LENGTH = 8;
const GLB_MAGIC = 0x46546c67;
const JSON_CHUNK_TYPE = 0x4e4f534a;

export function parseGlbBinary(arrayBuffer: ArrayBuffer): ParsedGlbBinary {
  if (arrayBuffer.byteLength < GLB_HEADER_LENGTH) {
    throw new Error('Invalid GLB header: file is too small.');
  }

  const header = new DataView(arrayBuffer, 0, GLB_HEADER_LENGTH);
  const magic = header.getUint32(0, true);
  const version = header.getUint32(4, true);
  const declaredLength = header.getUint32(8, true);

  if (magic !== GLB_MAGIC) {
    throw new Error('Invalid GLB header: expected magic bytes "glTF".');
  }

  if (version !== 2) {
    throw new Error(`Unsupported GLB version: ${version}.`);
  }

  if (declaredLength !== arrayBuffer.byteLength) {
    throw new Error('Invalid GLB header: declared byte length does not match the file size.');
  }

  if (arrayBuffer.byteLength < GLB_HEADER_LENGTH + GLB_CHUNK_HEADER_LENGTH) {
    throw new Error('Invalid GLB structure: missing JSON chunk.');
  }

  const chunkHeader = new DataView(arrayBuffer, GLB_HEADER_LENGTH, GLB_CHUNK_HEADER_LENGTH);
  const jsonChunkLength = chunkHeader.getUint32(0, true);
  const jsonChunkType = chunkHeader.getUint32(4, true);

  if (jsonChunkType !== JSON_CHUNK_TYPE) {
    throw new Error('Invalid GLB structure: first chunk must be JSON.');
  }

  const jsonStart = GLB_HEADER_LENGTH + GLB_CHUNK_HEADER_LENGTH;
  const jsonEnd = jsonStart + jsonChunkLength;
  if (jsonEnd > arrayBuffer.byteLength) {
    throw new Error('Invalid GLB structure: JSON chunk exceeds file bounds.');
  }

  const jsonChunk = new Uint8Array(arrayBuffer, jsonStart, jsonChunkLength);
  const jsonText = new TextDecoder().decode(jsonChunk).replace(/\0+$/g, '');

  let json: Record<string, unknown>;
  try {
    json = JSON.parse(jsonText) as Record<string, unknown>;
  } catch {
    throw new Error('Invalid GLB structure: JSON chunk could not be parsed.');
  }

  const scenes = Array.isArray(json.scenes) ? json.scenes : [];
  const requiredExtensions = Array.isArray(json.extensionsRequired)
    ? json.extensionsRequired.filter((value): value is string => typeof value === 'string')
    : [];
  const usedExtensions = Array.isArray(json.extensionsUsed)
    ? json.extensionsUsed.filter((value): value is string => typeof value === 'string')
    : [];

  const images = Array.isArray(json.images)
    ? json.images.map((image, index) => {
        const typedImage =
          image && typeof image === 'object' ? (image as Record<string, unknown>) : {};

        return {
          index,
          mimeType: typeof typedImage.mimeType === 'string' ? typedImage.mimeType : null,
          uri: typeof typedImage.uri === 'string' ? typedImage.uri : null,
          bufferView: typeof typedImage.bufferView === 'number' ? typedImage.bufferView : null,
          name: typeof typedImage.name === 'string' ? typedImage.name : null,
        };
      })
    : [];

  const textures = Array.isArray(json.textures)
    ? json.textures.map((texture, index) => {
        const typedTexture =
          texture && typeof texture === 'object' ? (texture as Record<string, unknown>) : {};
        const textureExtensions =
          typedTexture.extensions && typeof typedTexture.extensions === 'object'
            ? Object.keys(typedTexture.extensions as Record<string, unknown>)
            : [];

        return {
          index,
          source: typeof typedTexture.source === 'number' ? typedTexture.source : null,
          sampler: typeof typedTexture.sampler === 'number' ? typedTexture.sampler : null,
          name: typeof typedTexture.name === 'string' ? typedTexture.name : null,
          extensions: textureExtensions,
        };
      })
    : [];

  const materials = Array.isArray(json.materials)
    ? json.materials.map((material, index) => {
        const typedMaterial =
          material && typeof material === 'object' ? (material as Record<string, unknown>) : {};
        const pbr =
          typedMaterial.pbrMetallicRoughness &&
          typeof typedMaterial.pbrMetallicRoughness === 'object'
            ? (typedMaterial.pbrMetallicRoughness as Record<string, unknown>)
            : {};
        const extensions =
          typedMaterial.extensions && typeof typedMaterial.extensions === 'object'
            ? Object.keys(typedMaterial.extensions as Record<string, unknown>)
            : [];

        const readTextureIndex = (value: unknown): number | null => {
          if (!value || typeof value !== 'object') return null;
          const typedValue = value as Record<string, unknown>;
          return typeof typedValue.index === 'number' ? typedValue.index : null;
        };

        const alphaMode: 'OPAQUE' | 'MASK' | 'BLEND' =
          typedMaterial.alphaMode === 'MASK' || typedMaterial.alphaMode === 'BLEND'
            ? typedMaterial.alphaMode
            : 'OPAQUE';

        return {
          index,
          name: typeof typedMaterial.name === 'string' ? typedMaterial.name : null,
          alphaMode,
          alphaCutoff: typeof typedMaterial.alphaCutoff === 'number' ? typedMaterial.alphaCutoff : null,
          doubleSided: typedMaterial.doubleSided === true,
          extensionKeys: extensions,
          baseColorTextureIndex: readTextureIndex(pbr.baseColorTexture),
          metallicRoughnessTextureIndex: readTextureIndex(pbr.metallicRoughnessTexture),
          normalTextureIndex: readTextureIndex(typedMaterial.normalTexture),
          occlusionTextureIndex: readTextureIndex(typedMaterial.occlusionTexture),
          emissiveTextureIndex: readTextureIndex(typedMaterial.emissiveTexture),
        };
      })
    : [];

  return {
    version,
    json,
    sceneCount: scenes.length,
    requiredExtensions,
    usedExtensions,
    images,
    textures,
    materials,
  };
}
